import * as proto from 'src/proto/compiled-protos';
import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DeviceService } from '../../../general/services/device.service';
import { FormService } from '../../../general/services/form.service';
import { BackendService } from '../../services/backend.service';
import { Consumer, Runnable } from 'src/app/general/interfaces/functions';
import { ProtoUtil } from '../../util/proto-util';
import { MatRadioModule } from '@angular/material/radio';
import { MoneyPickerComponent, Money } from 'src/app/general/components/money-picker/money-picker.component';
import { QuantityPickerComponent, Quantity } from 'src/app/general/components/quantity-picker/quantity-picker.component';
import { HeadingComponent } from 'src/app/general/components/heading/heading.component';
import { CURRENCIES, DISTANCE_UNITS_NAMES } from '../../util/constants';
import { Formatter } from '../../util/formatter';
import { FieldContainerDirective } from 'src/app/general/directives/field/field-container.directive';
import { FieldDirective } from 'src/app/general/directives/field/field.directive';
import { AppError } from 'src/app/general/util/error';
import { ComponentUtil } from 'src/app/general/util/component-util';
import { ActionComponent } from 'src/app/general/components/action/action.component';
import { ActionObserver } from 'src/app/general/components/action/action';
import { ErrorResult, SuccessResult } from "src/app/general/util/result";
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { AlertComponent } from 'src/app/general/components/alert/alert.component';
import { AdditionalTaxEditorDialogData } from './interfaces';
import { AdditionalTaxEditorDialogService } from './additional-tax-editor-dialog/additional-tax-editor-dialog.service';
import { LocalizationService } from 'src/app/general/services/localization.service';
import { DialogService } from 'src/app/general/services/dialog.service';

// ----------------------------------------------------------------------------
// Usage Example

// Template:

// <div>
// <table>
//   <tr>
//     <td>
//         <app-action [text]="'expand_all' | translate" [defaultStyle]=true (onExecute)="accordion.openAll(); editLocationSettingsComponentAccordionOpenAll()"></app-action>
//     </td>
//     <td>
//         <app-action [text]="'collapse_all' | translate" [defaultStyle]=true (onExecute)="accordion.closeAll(); editLocationSettingsComponentAccordionCloseAll()"></app-action>
//     </td>
//   </tr>
// </table>

//  <mat-accordion multi>
//      ...
//  </mat-accordion multi>
// <!-- app-edit-location-settings also defines an accordion -->
// <app-edit-location-settings
//     [locationId]="locationId"
//     [locationSettings]="locationSettings">
// </app-edit-location-settings>

// <!-- app-edit-location-settings also defines an accordion -->
// <app-edit-location-settings
//     [businessId]="businessId"
//     [locationSettings]="locationSettings">
// </app-edit-location-settings>

// TS:

// @ViewChild(MatAccordion) accordion!: MatAccordion;
// @ViewChild(EditLocationSettingsComponent) editLocationSettingsComponent!: EditLocationSettingsComponent;

// public editLocationSettingsComponentAccordionOpenAll(): void {
//   this.editLocationSettingsComponent.accordionOpenAll();
// }

// public editLocationSettingsComponentAccordionCloseAll(): void {
//   this.editLocationSettingsComponent.accordionCloseAll();
// }

// ----------------------------------------------------------------------------

const NO_DELIVERY = "none";
const LANGUAGE_NONE = "none";
const THEME_NONE = "none";

@Component({
  selector: 'app-edit-location-settings',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatRadioModule,
    MoneyPickerComponent,
    QuantityPickerComponent,
    HeadingComponent,
    FieldContainerDirective,
    FieldDirective,
    ActionComponent,
    MatCheckboxModule,
    MatSelectModule,
    AlertComponent
  ],
  templateUrl: './edit-location-settings.component.html',
  styleUrls: ['./edit-location-settings.component.css']
})
export class EditLocationSettingsComponent implements OnInit, OnChanges {
  readonly CURRENCIES: Array<string> = CURRENCIES;
  readonly DISTANCE_UNITS_NAMES: Array<string> = DISTANCE_UNITS_NAMES;
  readonly NO_DELIVERY: string = NO_DELIVERY;
  readonly SELF_DELIVERY: string = "self";
  readonly DOORDASH_DELIVERY: string = "doordash";
  readonly TAX_TYPE_UNALTERABLE: string = "unalterable";
  readonly TAX_TYPE_FALLBACK: string = "fallback";
  readonly LANGUAGE_NONE: string = LANGUAGE_NONE;
  readonly LANGUAGE_ENGLISH: string = "english";
  readonly LANGUAGE_SPANISH: string = "spanish";
  readonly THEME_NONE: string = THEME_NONE;
  readonly THEME_DARK: string = "dark";
  readonly THEME_LIGHT: string = "light";

  @ViewChild(MatAccordion) accordion!: MatAccordion;
  @Input() businessId?: string;
  @Input() locationId?: string;
  @Input() locationSettings?: proto.waiternow.common.LocationProto.ISettingsProto | null | undefined;

  alertConfigFields = new (class {
    email: FormControl = new FormControl('', [Validators.email]);
    phoneNumber: FormControl = new FormControl('');
  })();

  enabledFeaturesFields = new (class {
    pokeWaiter: FormControl = new FormControl(true);
    menu: FormControl = new FormControl(true);
    imageMenu: FormControl = new FormControl(true);
    messageWaiter: FormControl = new FormControl(true);
    askForCheck: FormControl = new FormControl(true);
    specialInstructionsForMenuItem: FormControl = new FormControl(true);
    orderForLater: FormControl = new FormControl(true);
    orderForLaterDay: FormControl = new FormControl(true);
    onsiteTips: FormControl = new FormControl(true);
    onlinePickupTips: FormControl = new FormControl(true);
    delivery: FormControl = new FormControl(true);
    chargeCreditCardFeeToConsumer: FormControl = new FormControl(false);
    alcoholForOnlinePickup: FormControl = new FormControl(false);
    alcoholForDelivery: FormControl = new FormControl(false);
    printOrderAndReceipt: FormControl = new FormControl(false);
    isSmsOnNewOrdersForBusinessWebEnabled: FormControl = new FormControl(false);
    expandQuantityForPrintedOrders: FormControl = new FormControl(false);
    businessAutoStartSession: FormControl = new FormControl(false);
  })();

  viewFields = new (class {
    theme: FormControl = new FormControl(THEME_NONE);
  })();

  localeFields = new (class {
    language: FormControl = new FormControl(LANGUAGE_NONE);
  })();

  businessHoursFields = new (class {
    sundayFrom: FormControl = new FormControl('');
    sundayTo: FormControl = new FormControl('');
    mondayFrom: FormControl = new FormControl('');
    mondayTo: FormControl = new FormControl('');
    tuesdayFrom: FormControl = new FormControl('');
    tuesdayTo: FormControl = new FormControl('');
    wednesdayFrom: FormControl = new FormControl('');
    wednesdayTo: FormControl = new FormControl('');
    thursdayFrom: FormControl = new FormControl('');
    thursdayTo: FormControl = new FormControl('');
    fridayFrom: FormControl = new FormControl('');
    fridayTo: FormControl = new FormControl('');
    saturdayFrom: FormControl = new FormControl('');
    saturdayTo: FormControl = new FormControl('');

    sundaySecondFrom: FormControl = new FormControl('');
    sundaySecondTo: FormControl = new FormControl('');
    mondaySecondFrom: FormControl = new FormControl('');
    mondaySecondTo: FormControl = new FormControl('');
    tuesdaySecondFrom: FormControl = new FormControl('');
    tuesdaySecondTo: FormControl = new FormControl('');
    wednesdaySecondFrom: FormControl = new FormControl('');
    wednesdaySecondTo: FormControl = new FormControl('');
    thursdaySecondFrom: FormControl = new FormControl('');
    thursdaySecondTo: FormControl = new FormControl('');
    fridaySecondFrom: FormControl = new FormControl('');
    fridaySecondTo: FormControl = new FormControl('');
    saturdaySecondFrom: FormControl = new FormControl('');
    saturdaySecondTo: FormControl = new FormControl('');

    isBusinessHoursValid: boolean = true;

    isSundayClosed = new FormControl(false);
    isMondayClosed = new FormControl(false);
    isTuesdayClosed = new FormControl(false);
    isWednesdayClosed = new FormControl(false);
    isThursdayClosed = new FormControl(false);
    isFridayClosed = new FormControl(false);
    isSaturdayClosed = new FormControl(false);
  })();

  taxesConfigFields = new (class {
    salesTax: FormControl = new FormControl();
    isSalesTaxValid: boolean = true;
    salesTaxType: FormControl = new FormControl('');
    additionalTaxes: Array<proto.waiternow.common.TaxesProto.TaxProto> = [];
  })();

  orderingFields = new (class {
    orderPreparationDurationMinutes: FormControl = new FormControl();
  })();

  deliveryConfigFields = new (class {
    deliveryType: FormControl = new FormControl(NO_DELIVERY);
    isSelfConfigurationValid: boolean = false;
    isSelfConfigurationDisabled: boolean = true;
    baseFeeInitialValue: Money | null | undefined;
    baseDistanceInitialValue: Quantity | null | undefined;
    feePerDistanceUnitInitialValue: Money | null | undefined;
    maxDistanceInitialValue: Quantity | null | undefined;
    baseFeeCurrentValue: Money | null | undefined;
    baseDistanceCurrentValue: Quantity | null | undefined;
    feePerDistanceUnitCurrentValue: Money | null | undefined;
    maxDistanceCurrentValue: Quantity | null | undefined;
  })();

  externalLinksFields = new (class {
    facebook: FormControl = new FormControl('');
    facebookInitialValue: string = '';
    instagram: FormControl = new FormControl('');
    instagramInitialValue: string = '';
    linkedin: FormControl = new FormControl('');
    linkedinInitialValue: string = '';
    tiktok: FormControl = new FormControl('');
    tiktokInitialValue: string = '';
    twitter: FormControl = new FormControl('');
    twitterInitialValue: string = '';
    youtube: FormControl = new FormControl('');
    youtubeInitialValue: string = '';
    website: FormControl = new FormControl('');
    websiteInitialValue: string = '';
  })();

  constructor(
      public deviceService: DeviceService,
      public formService: FormService,
      private backendService: BackendService,
      private localizationService: LocalizationService,
      private additionalTaxEditorDialogService: AdditionalTaxEditorDialogService,
      private dialogService: DialogService,
      private translateService: TranslateService) {
  }

  public accordionOpenAll(): void {
    this.accordion.openAll();
  }

  public accordionCloseAll(): void {
    this.accordion.closeAll();
  }

  ngOnInit(): void {
    this.initFormControls();
    this.deliveryConfigFields.deliveryType.valueChanges.subscribe(() => {
      this.updateSelfConfigurationEnablement();
      this.updateIsSelfConfigurationValid();
    });
    this.businessHoursFields.sundayFrom.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.sundayTo.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.mondayFrom.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.mondayTo.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.tuesdayFrom.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.tuesdayTo.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.wednesdayFrom.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.wednesdayTo.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.thursdayFrom.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.thursdayTo.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.fridayFrom.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.fridayTo.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.saturdayFrom.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.saturdayTo.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.sundaySecondFrom.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.sundaySecondTo.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.mondaySecondFrom.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.mondaySecondTo.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.tuesdaySecondFrom.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.tuesdaySecondTo.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.wednesdaySecondFrom.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.wednesdaySecondTo.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.thursdaySecondFrom.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.thursdaySecondTo.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.fridaySecondFrom.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.fridaySecondTo.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.saturdaySecondFrom.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.businessHoursFields.saturdaySecondTo.valueChanges.subscribe(() => {
      this.updateIsBusinessHoursValid();
    });
    this.taxesConfigFields.salesTax.valueChanges.subscribe(() => {
      this.updateIsSalesTaxValid();
    });

    this.businessHoursFields.isSundayClosed.valueChanges.subscribe(() => {
      if (this.businessHoursFields.isSundayClosed.value) {
        this.businessHoursFields.sundayFrom.setValue('');
        this.businessHoursFields.sundayTo.setValue('');
        this.businessHoursFields.sundaySecondFrom.setValue('');
        this.businessHoursFields.sundaySecondTo.setValue('');
      }
    });
    this.businessHoursFields.isMondayClosed.valueChanges.subscribe(() => {
      if (this.businessHoursFields.isMondayClosed.value) {
        this.businessHoursFields.mondayFrom.setValue('');
        this.businessHoursFields.mondayTo.setValue('');
        this.businessHoursFields.mondaySecondFrom.setValue('');
        this.businessHoursFields.mondaySecondTo.setValue('');
      }
    });
    this.businessHoursFields.isTuesdayClosed.valueChanges.subscribe(() => {
      if (this.businessHoursFields.isTuesdayClosed.value) {
        this.businessHoursFields.tuesdayFrom.setValue('');
        this.businessHoursFields.tuesdayTo.setValue('');
        this.businessHoursFields.tuesdaySecondFrom.setValue('');
        this.businessHoursFields.tuesdaySecondTo.setValue('');
      }
    });
    this.businessHoursFields.isWednesdayClosed.valueChanges.subscribe(() => {
      if (this.businessHoursFields.isWednesdayClosed.value) {
        this.businessHoursFields.wednesdayFrom.setValue('');
        this.businessHoursFields.wednesdayTo.setValue('');
        this.businessHoursFields.wednesdaySecondFrom.setValue('');
        this.businessHoursFields.wednesdaySecondTo.setValue('');
      }
    });
    this.businessHoursFields.isThursdayClosed.valueChanges.subscribe(() => {
      if (this.businessHoursFields.isThursdayClosed.value) {
        this.businessHoursFields.thursdayFrom.setValue('');
        this.businessHoursFields.thursdayTo.setValue('');
        this.businessHoursFields.thursdaySecondFrom.setValue('');
        this.businessHoursFields.thursdaySecondTo.setValue('');
      }
    });
    this.businessHoursFields.isFridayClosed.valueChanges.subscribe(() => {
      if (this.businessHoursFields.isFridayClosed.value) {
        this.businessHoursFields.fridayFrom.setValue('');
        this.businessHoursFields.fridayTo.setValue('');
        this.businessHoursFields.fridaySecondFrom.setValue('');
        this.businessHoursFields.fridaySecondTo.setValue('');
      }
    });
    this.businessHoursFields.isSaturdayClosed.valueChanges.subscribe(() => {
      if (this.businessHoursFields.isSaturdayClosed.value) {
        this.businessHoursFields.saturdayFrom.setValue('');
        this.businessHoursFields.saturdayTo.setValue('');
        this.businessHoursFields.saturdaySecondFrom.setValue('');
        this.businessHoursFields.saturdaySecondTo.setValue('');
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (ComponentUtil.bindingChanged('locationSettings', changes)) {
      this.initFormControls();
    }
  }

  private initFormControls(): void {
    if (!this.locationSettings) {
      return;
    }

    const viewSetings = this.locationSettings?.viewSettings;
    const localeSetings = this.locationSettings?.localeSettings;
    const operationSetings = this.locationSettings?.operationSettings;
    const externalLinks = this.locationSettings?.externalLinks;

    // Alerts

    if (operationSetings && operationSetings.alertConfig) {
      const alertConfig = operationSetings.alertConfig;
      this.alertConfigFields.email.setValue(alertConfig.email);
      this.alertConfigFields.phoneNumber.setValue(Formatter.formatPhoneNumber(alertConfig.phoneNumber));
    }

    // Enabled Features

    let enabledFeatures: proto.waiternow.common.LocationProto.SettingsProto.OperationSettingsProto.IEnabledFeaturesProto;
    if (operationSetings && operationSetings.enabledFeatures) {
      enabledFeatures = operationSetings.enabledFeatures;
    } else {
      enabledFeatures = new proto.waiternow.common.LocationProto.SettingsProto.OperationSettingsProto.EnabledFeaturesProto();
    }
    this.enabledFeaturesFields.pokeWaiter.setValue(!enabledFeatures.isPokeWaiterDisabled);
    this.enabledFeaturesFields.menu.setValue(!enabledFeatures.isMenuDisabled);
    this.enabledFeaturesFields.imageMenu.setValue(!enabledFeatures.isImageMenuDisabled);
    this.enabledFeaturesFields.messageWaiter.setValue(!enabledFeatures.isMessageWaiterDisabled);
    this.enabledFeaturesFields.askForCheck.setValue(!enabledFeatures.isAskForCheckDisabled);
    this.enabledFeaturesFields.specialInstructionsForMenuItem.setValue(!enabledFeatures.isSpecialInstructionsDisabled);
    this.enabledFeaturesFields.orderForLater.setValue(!enabledFeatures.isOrderForLaterDisabled);
    this.enabledFeaturesFields.orderForLaterDay.setValue(!enabledFeatures.isOrderForLaterDayDisabled);
    this.enabledFeaturesFields.onlinePickupTips.setValue(enabledFeatures.isOnlinePickupTipsEnabled);
    this.enabledFeaturesFields.onsiteTips.setValue(!enabledFeatures.isOnsiteTipsDisabled);
    this.enabledFeaturesFields.delivery.setValue(!enabledFeatures.isDeliveryDisabled);
    this.enabledFeaturesFields.chargeCreditCardFeeToConsumer.setValue(enabledFeatures.chargeCreditCardFeeToConsumer);
    this.enabledFeaturesFields.alcoholForOnlinePickup.setValue(enabledFeatures.isAlcoholForOnlinePickupAllowed);
    this.enabledFeaturesFields.alcoholForDelivery.setValue(enabledFeatures.isAlcoholForDeliveryAllowed);
    this.enabledFeaturesFields.printOrderAndReceipt.setValue(enabledFeatures.printOrderAndReceipt);
    this.enabledFeaturesFields.isSmsOnNewOrdersForBusinessWebEnabled.setValue(enabledFeatures.isSmsOnNewOrdersForBusinessWebEnabled);
    this.enabledFeaturesFields.expandQuantityForPrintedOrders.setValue(enabledFeatures.expandQuantityForPrintedOrders);
    this.enabledFeaturesFields.businessAutoStartSession.setValue(enabledFeatures.isBusinessAutoStartSessionEnabled);

    // View

    if (viewSetings) {
      if (viewSetings.theme == proto.waiternow.common.Theme.DARK) {
        this.viewFields.theme.setValue(this.THEME_DARK);
      } else if(viewSetings.theme == proto.waiternow.common.Theme.LIGHT) {
        this.viewFields.theme.setValue(this.THEME_LIGHT);
      } else {
        this.viewFields.theme.setValue(this.THEME_NONE);
      }
    } else {
      this.viewFields.theme.setValue(this.THEME_NONE);
    }

    // Locale

    if (localeSetings) {
      if (localeSetings.language == proto.waiternow.common.Language.ENGLISH) {
        this.localeFields.language.setValue(this.LANGUAGE_ENGLISH);
      } else if(localeSetings.language == proto.waiternow.common.Language.SPANISH) {
        this.localeFields.language.setValue(this.LANGUAGE_SPANISH);
      } else {
        this.localeFields.language.setValue(this.LANGUAGE_NONE);
      }
    } else {
      this.localeFields.language.setValue(this.LANGUAGE_NONE);
    }

    // Business Hours

    if (operationSetings && operationSetings.businessHours) {
      const businessHours = operationSetings.businessHours;
      if (businessHours.sundayHours && businessHours.sundayHours.from && businessHours.sundayHours.to) {
        this.businessHoursFields.sundayFrom.setValue(Formatter.formatTimeToMinutes(businessHours.sundayHours.from))
        this.businessHoursFields.sundayTo.setValue(Formatter.formatTimeToMinutes(businessHours.sundayHours.to))
      }
      if (businessHours.mondayHours && businessHours.mondayHours.from && businessHours.mondayHours.to) {
        this.businessHoursFields.mondayFrom.setValue(Formatter.formatTimeToMinutes(businessHours.mondayHours.from))
        this.businessHoursFields.mondayTo.setValue(Formatter.formatTimeToMinutes(businessHours.mondayHours.to))
      }
      if (businessHours.tuesdayHours && businessHours.tuesdayHours.from && businessHours.tuesdayHours.to) {
        this.businessHoursFields.tuesdayFrom.setValue(Formatter.formatTimeToMinutes(businessHours.tuesdayHours.from))
        this.businessHoursFields.tuesdayTo.setValue(Formatter.formatTimeToMinutes(businessHours.tuesdayHours.to))
      }
      if (businessHours.wednesdayHours && businessHours.wednesdayHours.from && businessHours.wednesdayHours.to) {
        this.businessHoursFields.wednesdayFrom.setValue(Formatter.formatTimeToMinutes(businessHours.wednesdayHours.from))
        this.businessHoursFields.wednesdayTo.setValue(Formatter.formatTimeToMinutes(businessHours.wednesdayHours.to))
      }
      if (businessHours.thursdayHours && businessHours.thursdayHours.from && businessHours.thursdayHours.to) {
        this.businessHoursFields.thursdayFrom.setValue(Formatter.formatTimeToMinutes(businessHours.thursdayHours.from))
        this.businessHoursFields.thursdayTo.setValue(Formatter.formatTimeToMinutes(businessHours.thursdayHours.to))
      }
      if (businessHours.fridayHours && businessHours.fridayHours.from && businessHours.fridayHours.to) {
        this.businessHoursFields.fridayFrom.setValue(Formatter.formatTimeToMinutes(businessHours.fridayHours.from))
        this.businessHoursFields.fridayTo.setValue(Formatter.formatTimeToMinutes(businessHours.fridayHours.to))
      }
      if (businessHours.saturdayHours && businessHours.saturdayHours.from && businessHours.saturdayHours.to) {
        this.businessHoursFields.saturdayFrom.setValue(Formatter.formatTimeToMinutes(businessHours.saturdayHours.from))
        this.businessHoursFields.saturdayTo.setValue(Formatter.formatTimeToMinutes(businessHours.saturdayHours.to))
      }
      if (businessHours.sundaySecondHours && businessHours.sundaySecondHours.from && businessHours.sundaySecondHours.to) {
        this.businessHoursFields.sundaySecondFrom.setValue(Formatter.formatTimeToMinutes(businessHours.sundaySecondHours.from))
        this.businessHoursFields.sundaySecondTo.setValue(Formatter.formatTimeToMinutes(businessHours.sundaySecondHours.to))
      }
      if (businessHours.mondaySecondHours && businessHours.mondaySecondHours.from && businessHours.mondaySecondHours.to) {
        this.businessHoursFields.mondaySecondFrom.setValue(Formatter.formatTimeToMinutes(businessHours.mondaySecondHours.from))
        this.businessHoursFields.mondaySecondTo.setValue(Formatter.formatTimeToMinutes(businessHours.mondaySecondHours.to))
      }
      if (businessHours.tuesdaySecondHours && businessHours.tuesdaySecondHours.from && businessHours.tuesdaySecondHours.to) {
        this.businessHoursFields.tuesdaySecondFrom.setValue(Formatter.formatTimeToMinutes(businessHours.tuesdaySecondHours.from))
        this.businessHoursFields.tuesdaySecondTo.setValue(Formatter.formatTimeToMinutes(businessHours.tuesdaySecondHours.to))
      }
      if (businessHours.wednesdaySecondHours && businessHours.wednesdaySecondHours.from && businessHours.wednesdaySecondHours.to) {
        this.businessHoursFields.wednesdaySecondFrom.setValue(Formatter.formatTimeToMinutes(businessHours.wednesdaySecondHours.from))
        this.businessHoursFields.wednesdaySecondTo.setValue(Formatter.formatTimeToMinutes(businessHours.wednesdaySecondHours.to))
      }
      if (businessHours.thursdaySecondHours && businessHours.thursdaySecondHours.from && businessHours.thursdaySecondHours.to) {
        this.businessHoursFields.thursdaySecondFrom.setValue(Formatter.formatTimeToMinutes(businessHours.thursdaySecondHours.from))
        this.businessHoursFields.thursdaySecondTo.setValue(Formatter.formatTimeToMinutes(businessHours.thursdaySecondHours.to))
      }
      if (businessHours.fridaySecondHours && businessHours.fridaySecondHours.from && businessHours.fridaySecondHours.to) {
        this.businessHoursFields.fridaySecondFrom.setValue(Formatter.formatTimeToMinutes(businessHours.fridaySecondHours.from))
        this.businessHoursFields.fridaySecondTo.setValue(Formatter.formatTimeToMinutes(businessHours.fridaySecondHours.to))
      }
      if (businessHours.saturdaySecondHours && businessHours.saturdaySecondHours.from && businessHours.saturdaySecondHours.to) {
        this.businessHoursFields.saturdaySecondFrom.setValue(Formatter.formatTimeToMinutes(businessHours.saturdaySecondHours.from))
        this.businessHoursFields.saturdaySecondTo.setValue(Formatter.formatTimeToMinutes(businessHours.saturdaySecondHours.to))
      }
      this.businessHoursFields.isSundayClosed.setValue(Boolean(businessHours.isSundayClosed));
      this.businessHoursFields.isMondayClosed.setValue(Boolean(businessHours.isMondayClosed));
      this.businessHoursFields.isTuesdayClosed.setValue(Boolean(businessHours.isTuesdayClosed));
      this.businessHoursFields.isWednesdayClosed.setValue(Boolean(businessHours.isWednesdayClosed));
      this.businessHoursFields.isThursdayClosed.setValue(Boolean(businessHours.isThursdayClosed));
      this.businessHoursFields.isFridayClosed.setValue(Boolean(businessHours.isFridayClosed));
      this.businessHoursFields.isSaturdayClosed.setValue(Boolean(businessHours.isSaturdayClosed));
    }

    this.updateIsBusinessHoursValid();

    // Taxes

    // This taxes configuration is treated as the source of truth so they are used in payments.
    // This is the default value if type is not set and taxes is not empty.
    // UNALTERABLE = 1;
    // Taxes are fetched from government APIs and this taxes configuration is only used in the event of failures.
    // This is the default value if taxes is empty.
    // FALLBACK = 2;
    if (operationSetings && operationSetings.taxes) {
      const taxes = operationSetings.taxes;
      if (taxes.salesTaxType == proto.waiternow.common.TaxesProto.Type.FALLBACK) {
        this.taxesConfigFields.salesTaxType.setValue(this.TAX_TYPE_FALLBACK);
      } else {
        this.taxesConfigFields.salesTaxType.setValue(this.TAX_TYPE_UNALTERABLE);
      }
      if (taxes.salesTax) {
        this.taxesConfigFields.salesTax.setValue(taxes.salesTax.value);
      }
    } else {
      this.taxesConfigFields.salesTaxType.setValue(this.TAX_TYPE_FALLBACK);
    }
    this.updateIsSalesTaxValid();

    // Ordering
    if (this.locationSettings.operationSettings?.orderPreparationDuration) {
      this.orderingFields.orderPreparationDurationMinutes.setValue(ProtoUtil.durationProtoToMillis(this.locationSettings.operationSettings?.orderPreparationDuration) / (1000 * 60));
    }

    // Delivery

    if (operationSetings && operationSetings.deliveryConfig) {
      const deliveryConfig = operationSetings.deliveryConfig;
      if (deliveryConfig.deliveryCarrier == proto.waiternow.common.DeliveryCarrier.SELF) {
        this.deliveryConfigFields.deliveryType.setValue(this.SELF_DELIVERY);
      } else if(deliveryConfig.deliveryCarrier == proto.waiternow.common.DeliveryCarrier.DOORDASH) {
        this.deliveryConfigFields.deliveryType.setValue(this.DOORDASH_DELIVERY);
      } else {
        this.deliveryConfigFields.deliveryType.setValue(this.NO_DELIVERY);
      }
      if (deliveryConfig.selfDeliveryConfig) {
        const selfDeliveryConfig = deliveryConfig.selfDeliveryConfig;
        if (selfDeliveryConfig.baseFee) {
          this.deliveryConfigFields.baseFeeInitialValue
              = ProtoUtil.moneyProtoToPickerMoney(selfDeliveryConfig.baseFee);
          this.deliveryConfigFields.baseFeeCurrentValue = this.deliveryConfigFields.baseFeeInitialValue;
        }
        if (selfDeliveryConfig.baseDistance) {
          this.deliveryConfigFields.baseDistanceInitialValue
              = ProtoUtil.distanceQuantityProtoToPickerQuantity(selfDeliveryConfig.baseDistance);
          this.deliveryConfigFields.baseDistanceCurrentValue = this.deliveryConfigFields.baseDistanceInitialValue;
        }
        if (selfDeliveryConfig.feePerDistanceUnit) {
          this.deliveryConfigFields.feePerDistanceUnitInitialValue
              = ProtoUtil.moneyProtoToPickerMoney(selfDeliveryConfig.feePerDistanceUnit);
          this.deliveryConfigFields.feePerDistanceUnitCurrentValue = this.deliveryConfigFields.feePerDistanceUnitInitialValue;
        }
        if (selfDeliveryConfig.maxDistance) {
          this.deliveryConfigFields.maxDistanceInitialValue
              = ProtoUtil.distanceQuantityProtoToPickerQuantity(selfDeliveryConfig.maxDistance);
          this.deliveryConfigFields.maxDistanceCurrentValue = this.deliveryConfigFields.maxDistanceInitialValue;
        }
      }
    } else {
      this.deliveryConfigFields.deliveryType.setValue(this.NO_DELIVERY);
    }
    this.updateSelfConfigurationEnablement();
    this.updateIsSelfConfigurationValid();

    if (externalLinks) {
      if (externalLinks?.facebook) {
        this.externalLinksFields.facebook.setValue(externalLinks.facebook);
        this.externalLinksFields.facebookInitialValue = externalLinks.facebook;
      }
      if (externalLinks?.instagram) {
        this.externalLinksFields.instagram.setValue(externalLinks.instagram);
        this.externalLinksFields.instagramInitialValue = externalLinks.instagram;
      }
      if (externalLinks?.linkedIn) {
        this.externalLinksFields.linkedin.setValue(externalLinks.linkedIn);
        this.externalLinksFields.linkedinInitialValue = externalLinks.linkedIn;
      }
      if (externalLinks?.tiktok) {
        this.externalLinksFields.tiktok.setValue(externalLinks.tiktok);
        this.externalLinksFields.tiktokInitialValue = externalLinks.tiktok;
      }
      if (externalLinks?.twitter) {
        this.externalLinksFields.twitter.setValue(externalLinks.twitter);
        this.externalLinksFields.twitterInitialValue = externalLinks.twitter;
      }
      if (externalLinks?.youtube) {
        this.externalLinksFields.youtube.setValue(externalLinks.youtube);
        this.externalLinksFields.youtubeInitialValue = externalLinks.youtube;
      }
      if (externalLinks?.website) {
        this.externalLinksFields.website.setValue(externalLinks.website);
        this.externalLinksFields.websiteInitialValue = externalLinks.website;
      }
    } else {
      this.externalLinksFields.facebook.setValue('');
      this.externalLinksFields.facebookInitialValue = '';
      this.externalLinksFields.instagram.setValue('');
      this.externalLinksFields.instagramInitialValue = '';
      this.externalLinksFields.linkedin.setValue('');
      this.externalLinksFields.linkedinInitialValue = '';
      this.externalLinksFields.tiktok.setValue('');
      this.externalLinksFields.tiktokInitialValue = '';
      this.externalLinksFields.twitter.setValue('');
      this.externalLinksFields.twitterInitialValue = '';
      this.externalLinksFields.youtube.setValue('');
      this.externalLinksFields.youtubeInitialValue = '';
      this.externalLinksFields.website.setValue('');
      this.externalLinksFields.websiteInitialValue = '';
    }
  }

  private updateSelfConfigurationEnablement() {
    this.deliveryConfigFields.isSelfConfigurationDisabled
        = this.deliveryConfigFields.deliveryType.value != this.SELF_DELIVERY;
  }

  private createBusiness(): proto.waiternow.common.BusinessProto {
    const business = new proto.waiternow.common.BusinessProto();
    if (this.businessId) {
      business.id = this.businessId;
    }
    return business;
  }

  private createLocation(): proto.waiternow.common.LocationProto {
    const location = new proto.waiternow.common.LocationProto();
    if (this.locationId) {
      location.id = this.locationId;
    }
    return location;
  }

  private createLocationSettings(): proto.waiternow.common.LocationProto.SettingsProto {
    const locationSettings = new proto.waiternow.common.LocationProto.SettingsProto();
    return locationSettings;
  }

  private saveSettings(
      locationSettings: proto.waiternow.common.LocationProto.SettingsProto,
      settingsFieldsToRemove: Array<proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField>,
      onSuccess: Runnable,
      onError: Consumer<AppError>): void {
    if (this.businessId) {
      const business = this.createBusiness();
      business.locationSettings = locationSettings;
      this.backendService.updateBusiness(
        business,
        /* fieldsToRemove= */ [],
        settingsFieldsToRemove,
        /* onSuccess= */ () => onSuccess(),
        /* onError= */ error => onError(error)
      );
    } else if (this.locationId) {
      const location = this.createLocation();;
      location.settings = locationSettings;
      this.backendService.updateLocation(
        location,
        /* fieldsToRemove= */ [],
        settingsFieldsToRemove,
        /* onSuccess= */ () => onSuccess(),
        /* onError= */ error => onError(error)
      );
    }
  }

  public saveAlerts(actionObserver: ActionObserver): void {
    const locationSettings = this.createLocationSettings();
    const settingsFieldsToRemove: Array<proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField> = [];

    if (!this.alertConfigFields.email.value && !this.alertConfigFields.phoneNumber.value) {
      settingsFieldsToRemove.push(
          proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ALERT_CONFIG);
    } else {
      locationSettings.operationSettings = new proto.waiternow.common.LocationProto.SettingsProto.OperationSettingsProto();
      locationSettings.operationSettings.alertConfig
          = new proto.waiternow.common.LocationProto.SettingsProto.OperationSettingsProto.AlertConfigProto();
      if (this.alertConfigFields.email.value) {
        locationSettings.operationSettings.alertConfig.email = this.alertConfigFields.email.value;
      }
      if (this.alertConfigFields.phoneNumber.value) {
        locationSettings.operationSettings.alertConfig.phoneNumber
            = ProtoUtil.createSimplePhoneNumberProto(this.alertConfigFields.phoneNumber.value);
      }
    }

    this.saveSettings(
      locationSettings,
      settingsFieldsToRemove,
      /* onSuccess= */ () => {
        actionObserver.onSuccess(SuccessResult.withMessageTranslateId('saved'));
      },
      /* onError */ error => {
        actionObserver.onError(ErrorResult.withErrorAndMessageTranslateId(error, 'error_updating_alert_configuration'));
      }
    );
  }

  public saveEnabledFeatures(actionObserver: ActionObserver): void {
    const locationSettings = this.createLocationSettings();
    const settingsFieldsToRemove: Array<proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField> = [];

    locationSettings.operationSettings = new proto.waiternow.common.LocationProto.SettingsProto.OperationSettingsProto();
    locationSettings.operationSettings.enabledFeatures
        = new proto.waiternow.common.LocationProto.SettingsProto.OperationSettingsProto.EnabledFeaturesProto();

    if (!this.enabledFeaturesFields.pokeWaiter.value) {
      locationSettings.operationSettings.enabledFeatures.isPokeWaiterDisabled = true;
    } else {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ENABLED_FEATURES_POKE_WAITER_DISABLED);
    }
    if (!this.enabledFeaturesFields.menu.value) {
      locationSettings.operationSettings.enabledFeatures.isMenuDisabled = true;
    } else {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ENABLED_FEATURES_MENU_DISABLED);
    }
    if (!this.enabledFeaturesFields.imageMenu.value) {
      locationSettings.operationSettings.enabledFeatures.isImageMenuDisabled = true;
    } else {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ENABLED_FEATURES_IMAGE_MENU_DISABLED);
    }
    if (!this.enabledFeaturesFields.messageWaiter.value) {
      locationSettings.operationSettings.enabledFeatures.isMessageWaiterDisabled = true;
    } else {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ENABLED_FEATURES_MESSAGE_WAITER_DISABLED);
    }
    if (!this.enabledFeaturesFields.askForCheck.value) {
      locationSettings.operationSettings.enabledFeatures.isAskForCheckDisabled = true;
    } else {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ENABLED_FEATURES_ASK_FOR_CHECK_DISABLED);
    }
    if (!this.enabledFeaturesFields.specialInstructionsForMenuItem.value) {
      locationSettings.operationSettings.enabledFeatures.isSpecialInstructionsDisabled = true;
    } else {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ENABLED_FEATURES_SPECIAL_INSTRUCTIONS_DISABLED);
    }
    if (!this.enabledFeaturesFields.orderForLater.value) {
      locationSettings.operationSettings.enabledFeatures.isOrderForLaterDisabled = true;
    } else {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ENABLED_FEATURES_IS_ORDER_FOR_LATER_DISABLED);
    }
    if (!this.enabledFeaturesFields.orderForLaterDay.value) {
      locationSettings.operationSettings.enabledFeatures.isOrderForLaterDayDisabled = true;
    } else {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ENABLED_FEATURES_IS_ORDER_FOR_LATER_DAY_DISABLED);
    }
    if (this.enabledFeaturesFields.onlinePickupTips.value) {
      locationSettings.operationSettings.enabledFeatures.isOnlinePickupTipsEnabled = true;
    } else {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ENABLED_FEATURES_IS_ONLINE_PICKUP_TIPS_ENABLED);
    }
    if (!this.enabledFeaturesFields.onsiteTips.value) {
      locationSettings.operationSettings.enabledFeatures.isOnsiteTipsDisabled = true;
    } else {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ENABLED_FEATURES_IS_ONSITE_TIPS_DISABLED);
    }
    if (!this.enabledFeaturesFields.delivery.value) {
      locationSettings.operationSettings.enabledFeatures.isDeliveryDisabled = true;
    } else {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ENABLED_FEATURES_IS_DELIVERY_DISABLED);
    }
    if (this.enabledFeaturesFields.chargeCreditCardFeeToConsumer.value) {
      locationSettings.operationSettings.enabledFeatures.chargeCreditCardFeeToConsumer = true;
    } else {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ENABLED_FEATURES_CHARGE_CREDIT_CARD_FEE_TO_CONSUMER);
    }
    if (this.enabledFeaturesFields.alcoholForOnlinePickup.value) {
      locationSettings.operationSettings.enabledFeatures.isAlcoholForOnlinePickupAllowed = true;
    } else {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ENABLED_FEATURES_IS_ALCOHOL_FOR_PICKUP_ALLOWED);
    }
    if (this.enabledFeaturesFields.alcoholForDelivery.value) {
      locationSettings.operationSettings.enabledFeatures.isAlcoholForDeliveryAllowed = true;
    } else {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ENABLED_FEATURES_IS_ALCOHOL_FOR_DELIVERY_ALLOWED);
    }
    if (this.enabledFeaturesFields.printOrderAndReceipt.value) {
      locationSettings.operationSettings.enabledFeatures.printOrderAndReceipt = true;
    } else {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ENABLED_FEATURES_PRINT_ORDER_AND_RECEIPT);
    }
    if (this.enabledFeaturesFields.isSmsOnNewOrdersForBusinessWebEnabled.value) {
      locationSettings.operationSettings.enabledFeatures.isSmsOnNewOrdersForBusinessWebEnabled = true;
    } else {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ENABLED_FEATURES_IS_SMS_ON_NEW_ORDERS_FOR_BUSINESS_WEB_ENABLED);
    }
    if (this.enabledFeaturesFields.expandQuantityForPrintedOrders.value) {
      locationSettings.operationSettings.enabledFeatures.expandQuantityForPrintedOrders = true;
    } else {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ENABLED_FEATURES_EXPAND_QUANTITY_FOR_PRINTED_ORDERS);
    }
    if (this.enabledFeaturesFields.businessAutoStartSession.value) {
      locationSettings.operationSettings.enabledFeatures.isBusinessAutoStartSessionEnabled = true;
    } else {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ENABLED_FEATURES_IS_BUSINESS_AUTO_START_SESSION_ENABLED);
    }

    this.saveSettings(
      locationSettings,
      settingsFieldsToRemove,
      /* onSuccess= */ () => {
        actionObserver.onSuccess(SuccessResult.withMessageTranslateId('saved'));
      },
      /* onError */ error => {
        actionObserver.onError(ErrorResult.withErrorAndMessageTranslateId(error, 'error_updating_enabled_features'));
      }
    );
  }

  public saveView(actionObserver: ActionObserver): void {
    const locationSettings = this.createLocationSettings();
    const settingsFieldsToRemove: Array<proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField> = [];

    if (this.viewFields.theme.value === this.THEME_NONE) {
      settingsFieldsToRemove.push(proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.VIEW_THEME);
    } else {
      locationSettings.viewSettings = new proto.waiternow.common.LocationProto.SettingsProto.ViewSettingsProto();
      if (this.viewFields.theme.value === this.THEME_DARK) {
        locationSettings.viewSettings.theme = proto.waiternow.common.Theme.DARK;
      } else {
        locationSettings.viewSettings.theme = proto.waiternow.common.Theme.LIGHT;
      }
    }

    this.saveSettings(
      locationSettings,
      settingsFieldsToRemove,
      /* onSuccess= */ () => {
        actionObserver.onSuccess(SuccessResult.withMessageTranslateId('saved'));
      },
      /* onError */ error => {
        actionObserver.onError(ErrorResult.withErrorAndMessageTranslateId(error, 'error_updating_view_configuration'));
      }
    );
  }

  public saveLocale(actionObserver: ActionObserver): void {
    const locationSettings = this.createLocationSettings();
    const settingsFieldsToRemove: Array<proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField> = [];

    if (this.localeFields.language.value === this.LANGUAGE_NONE) {
      settingsFieldsToRemove.push(proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.LOCALE_LANGUAGE);
    } else {
      locationSettings.localeSettings = new proto.waiternow.common.LocationProto.SettingsProto.LocaleSettingsProto();
      if (this.localeFields.language.value === this.LANGUAGE_ENGLISH) {
        locationSettings.localeSettings.language = proto.waiternow.common.Language.ENGLISH;
      } else {
        locationSettings.localeSettings.language = proto.waiternow.common.Language.SPANISH;
      }
    }

    this.saveSettings(
      locationSettings,
      settingsFieldsToRemove,
      /* onSuccess= */ () => {
        actionObserver.onSuccess(SuccessResult.withMessageTranslateId('saved'));
      },
      /* onError */ error => {
        actionObserver.onError(ErrorResult.withErrorAndMessageTranslateId(error, 'error_updating_locale'));
      }
    );
  }

  public saveBusinessHours(actionObserver: ActionObserver): void {
    const locationSettings = this.createLocationSettings();
    const settingsFieldsToRemove: Array<proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField> = [];

    if (
      !this.businessHoursFields.sundayFrom.value && !this.businessHoursFields.sundayTo.value
      && !this.businessHoursFields.mondayFrom.value && !this.businessHoursFields.mondayTo.value
      && !this.businessHoursFields.tuesdayFrom.value && !this.businessHoursFields.tuesdayTo.value
      && !this.businessHoursFields.wednesdayFrom.value && !this.businessHoursFields.wednesdayTo.value
      && !this.businessHoursFields.thursdayFrom.value && !this.businessHoursFields.thursdayTo.value
      && !this.businessHoursFields.fridayFrom.value && !this.businessHoursFields.fridayTo.value
      && !this.businessHoursFields.saturdayFrom.value && !this.businessHoursFields.saturdayTo.value
      && !this.businessHoursFields.sundaySecondFrom.value && !this.businessHoursFields.sundaySecondTo.value
      && !this.businessHoursFields.mondaySecondFrom.value && !this.businessHoursFields.mondaySecondTo.value
      && !this.businessHoursFields.tuesdaySecondFrom.value && !this.businessHoursFields.tuesdaySecondTo.value
      && !this.businessHoursFields.wednesdaySecondFrom.value && !this.businessHoursFields.wednesdaySecondTo.value
      && !this.businessHoursFields.thursdaySecondFrom.value && !this.businessHoursFields.thursdaySecondTo.value
      && !this.businessHoursFields.fridaySecondFrom.value && !this.businessHoursFields.fridaySecondTo.value
      && !this.businessHoursFields.saturdaySecondFrom.value && !this.businessHoursFields.saturdaySecondTo.value
      && !this.businessHoursFields.isSundayClosed.value
      && !this.businessHoursFields.isMondayClosed.value
      && !this.businessHoursFields.isTuesdayClosed.value
      && !this.businessHoursFields.isWednesdayClosed.value
      && !this.businessHoursFields.isThursdayClosed.value
      && !this.businessHoursFields.isFridayClosed.value
      && !this.businessHoursFields.isSaturdayClosed.value
    ) {
      settingsFieldsToRemove.push(proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.BUSINESS_HOURS);
    } else {
      locationSettings.operationSettings = new proto.waiternow.common.LocationProto.SettingsProto.OperationSettingsProto();
      const businessHours = new proto.waiternow.common.BusinessHoursProto();
      locationSettings.operationSettings.businessHours = businessHours;

      if (this.businessHoursFields.sundayFrom.value && this.businessHoursFields.sundayTo.value) {
        businessHours.sundayHours = this.createTimeRange(
          this.businessHoursFields.sundayFrom.value, this.businessHoursFields.sundayTo.value);
      }
      if (this.businessHoursFields.mondayFrom.value && this.businessHoursFields.mondayTo.value) {
        businessHours.mondayHours = this.createTimeRange(
          this.businessHoursFields.mondayFrom.value, this.businessHoursFields.mondayTo.value);
      }
      if (this.businessHoursFields.tuesdayFrom.value && this.businessHoursFields.tuesdayTo.value) {
        businessHours.tuesdayHours = this.createTimeRange(
          this.businessHoursFields.tuesdayFrom.value, this.businessHoursFields.tuesdayTo.value);
      }
      if (this.businessHoursFields.wednesdayFrom.value && this.businessHoursFields.wednesdayTo.value) {
        businessHours.wednesdayHours = this.createTimeRange(
          this.businessHoursFields.wednesdayFrom.value, this.businessHoursFields.wednesdayTo.value);
      }
      if (this.businessHoursFields.thursdayFrom.value && this.businessHoursFields.thursdayTo.value) {
        businessHours.thursdayHours = this.createTimeRange(
          this.businessHoursFields.thursdayFrom.value, this.businessHoursFields.thursdayTo.value);
      }
      if (this.businessHoursFields.fridayFrom.value && this.businessHoursFields.fridayTo.value) {
        businessHours.fridayHours = this.createTimeRange(
          this.businessHoursFields.fridayFrom.value, this.businessHoursFields.fridayTo.value);
      }
      if (this.businessHoursFields.saturdayFrom.value && this.businessHoursFields.saturdayTo.value) {
        businessHours.saturdayHours = this.createTimeRange(
          this.businessHoursFields.saturdayFrom.value, this.businessHoursFields.saturdayTo.value);
      }
      if (this.businessHoursFields.sundaySecondFrom.value && this.businessHoursFields.sundaySecondTo.value) {
        businessHours.sundaySecondHours = this.createTimeRange(
          this.businessHoursFields.sundaySecondFrom.value, this.businessHoursFields.sundaySecondTo.value);
      }
      if (this.businessHoursFields.mondaySecondFrom.value && this.businessHoursFields.mondaySecondTo.value) {
        businessHours.mondaySecondHours = this.createTimeRange(
          this.businessHoursFields.mondaySecondFrom.value, this.businessHoursFields.mondaySecondTo.value);
      }
      if (this.businessHoursFields.tuesdaySecondFrom.value && this.businessHoursFields.tuesdaySecondTo.value) {
        businessHours.tuesdaySecondHours = this.createTimeRange(
          this.businessHoursFields.tuesdaySecondFrom.value, this.businessHoursFields.tuesdaySecondTo.value);
      }
      if (this.businessHoursFields.wednesdaySecondFrom.value && this.businessHoursFields.wednesdaySecondTo.value) {
        businessHours.wednesdaySecondHours = this.createTimeRange(
          this.businessHoursFields.wednesdaySecondFrom.value, this.businessHoursFields.wednesdaySecondTo.value);
      }
      if (this.businessHoursFields.thursdaySecondFrom.value && this.businessHoursFields.thursdaySecondTo.value) {
        businessHours.thursdaySecondHours = this.createTimeRange(
          this.businessHoursFields.thursdaySecondFrom.value, this.businessHoursFields.thursdaySecondTo.value);
      }
      if (this.businessHoursFields.fridaySecondFrom.value && this.businessHoursFields.fridaySecondTo.value) {
        businessHours.fridaySecondHours = this.createTimeRange(
          this.businessHoursFields.fridaySecondFrom.value, this.businessHoursFields.fridaySecondTo.value);
      }
      if (this.businessHoursFields.saturdaySecondFrom.value && this.businessHoursFields.saturdaySecondTo.value) {
        businessHours.saturdaySecondHours = this.createTimeRange(
          this.businessHoursFields.saturdaySecondFrom.value, this.businessHoursFields.saturdaySecondTo.value);
      }
      businessHours.isSundayClosed = Boolean(this.businessHoursFields.isSundayClosed.value);
      businessHours.isMondayClosed = Boolean(this.businessHoursFields.isMondayClosed.value);
      businessHours.isTuesdayClosed = Boolean(this.businessHoursFields.isTuesdayClosed.value);
      businessHours.isWednesdayClosed = Boolean(this.businessHoursFields.isWednesdayClosed.value);
      businessHours.isThursdayClosed = Boolean(this.businessHoursFields.isThursdayClosed.value);
      businessHours.isFridayClosed = Boolean(this.businessHoursFields.isFridayClosed.value);
      businessHours.isSaturdayClosed = Boolean(this.businessHoursFields.isSaturdayClosed.value);
    }

    this.saveSettings(
      locationSettings,
      settingsFieldsToRemove,
      /* onSuccess= */ () => {
        actionObserver.onSuccess(SuccessResult.withMessageTranslateId('saved'));
      },
      /* onError */ error => {
        actionObserver.onError(ErrorResult.withErrorAndMessageTranslateId(error, 'error_updating_locale'));
      }
    );
  }

  private createTimeRange(timeTextFrom: string, timeTextTo: string): proto.waiternow.common.TimeRangeProto {
    const timeRange = new proto.waiternow.common.TimeRangeProto();
    timeRange.from = ProtoUtil.timeTextToTimeProto(timeTextFrom);
    timeRange.to = ProtoUtil.timeTextToTimeProto(timeTextTo);
    return timeRange;
  }

  // Valid ranges for only main hours:
  // 9:00 to 20:00
  // 9:00 to 2:00 - Closes the next day at 2 am
  // Valid ranges for main and second hours:
  // 9:00 to 14:00 and 16:00 to 20:00
  // 9:00 to 14:00 and 16:00 to 2:00  - Closes the next day at 2 am
  // If main_from is smaller than main_to (9:00 to 2:00 for example), there cannot be second hours.
  // If second_from is smaller than second_to (16:00 to 2:00 for example), second_to should be smaller than the next day main_from.
  private updateIsBusinessHoursValid(): void {
    if (
      !this.isTimeTextRangeValid(this.businessHoursFields.sundayFrom.value, this.businessHoursFields.sundayTo.value)
      || !this.isTimeTextRangeValid(this.businessHoursFields.mondayFrom.value, this.businessHoursFields.mondayTo.value)
      || !this.isTimeTextRangeValid(this.businessHoursFields.tuesdayFrom.value, this.businessHoursFields.tuesdayTo.value)
      || !this.isTimeTextRangeValid(this.businessHoursFields.wednesdayFrom.value, this.businessHoursFields.wednesdayTo.value)
      || !this.isTimeTextRangeValid(this.businessHoursFields.thursdayFrom.value, this.businessHoursFields.thursdayTo.value)
      || !this.isTimeTextRangeValid(this.businessHoursFields.fridayFrom.value, this.businessHoursFields.fridayTo.value)
      || !this.isTimeTextRangeValid(this.businessHoursFields.saturdayFrom.value, this.businessHoursFields.saturdayTo.value)
      || !this.isTimeTextRangeValid(this.businessHoursFields.sundaySecondFrom.value, this.businessHoursFields.sundaySecondTo.value)
      || !this.isTimeTextRangeValid(this.businessHoursFields.mondaySecondFrom.value, this.businessHoursFields.mondaySecondTo.value)
      || !this.isTimeTextRangeValid(this.businessHoursFields.tuesdaySecondFrom.value, this.businessHoursFields.tuesdaySecondTo.value)
      || !this.isTimeTextRangeValid(this.businessHoursFields.wednesdaySecondFrom.value, this.businessHoursFields.wednesdaySecondTo.value)
      || !this.isTimeTextRangeValid(this.businessHoursFields.thursdaySecondFrom.value, this.businessHoursFields.thursdaySecondTo.value)
      || !this.isTimeTextRangeValid(this.businessHoursFields.fridaySecondFrom.value, this.businessHoursFields.fridaySecondTo.value)
      || !this.isTimeTextRangeValid(this.businessHoursFields.saturdaySecondFrom.value, this.businessHoursFields.saturdaySecondTo.value)
    ) {
      this.businessHoursFields.isBusinessHoursValid = false;
      return;
    }

      // TODO: If second_from is smaller than second_to (16:00 to 2:00 for example), second_to should
      // be smaller than the next day main_from.

    if (this.businessHoursFields.sundaySecondFrom.value) {
      if (!this.businessHoursFields.sundayTo.value
          || this.compareTextTimes(this.businessHoursFields.sundayFrom.value, this.businessHoursFields.sundayTo.value) >= 0
          || this.compareTextTimes(this.businessHoursFields.sundayTo.value, this.businessHoursFields.sundaySecondFrom.value) >= 0) {
        this.businessHoursFields.isBusinessHoursValid = false;
        return;
      }
    }
    if (this.businessHoursFields.mondaySecondFrom.value) {
      if (!this.businessHoursFields.mondayTo.value
          || this.compareTextTimes(this.businessHoursFields.mondayFrom.value, this.businessHoursFields.mondayTo.value) >= 0
          || this.compareTextTimes(this.businessHoursFields.mondayTo.value, this.businessHoursFields.mondaySecondFrom.value) >= 0) {
        this.businessHoursFields.isBusinessHoursValid = false;
        return;
      }
    }
    if (this.businessHoursFields.tuesdaySecondFrom.value) {
      if (!this.businessHoursFields.tuesdayTo.value
          || this.compareTextTimes(this.businessHoursFields.tuesdayFrom.value, this.businessHoursFields.tuesdayTo.value) >= 0
          || this.compareTextTimes(this.businessHoursFields.tuesdayTo.value, this.businessHoursFields.tuesdaySecondFrom.value) >= 0) {
        this.businessHoursFields.isBusinessHoursValid = false;
        return;
      }
    }
    if (this.businessHoursFields.wednesdaySecondFrom.value) {
      if (!this.businessHoursFields.wednesdayTo.value
          || this.compareTextTimes(this.businessHoursFields.wednesdayFrom.value, this.businessHoursFields.wednesdayTo.value) >= 0
          || this.compareTextTimes(this.businessHoursFields.wednesdayTo.value, this.businessHoursFields.wednesdaySecondFrom.value) >= 0) {
        this.businessHoursFields.isBusinessHoursValid = false;
        return;
      }
    }
    if (this.businessHoursFields.thursdaySecondFrom.value) {
      if (!this.businessHoursFields.thursdayTo.value
          || this.compareTextTimes(this.businessHoursFields.thursdayFrom.value, this.businessHoursFields.thursdayTo.value) >= 0
          || this.compareTextTimes(this.businessHoursFields.thursdayTo.value, this.businessHoursFields.thursdaySecondFrom.value) >= 0) {
        this.businessHoursFields.isBusinessHoursValid = false;
        return;
      }
    }
    if (this.businessHoursFields.fridaySecondFrom.value) {
      if (!this.businessHoursFields.fridayTo.value
          || this.compareTextTimes(this.businessHoursFields.fridayFrom.value, this.businessHoursFields.fridayTo.value) >= 0
          || this.compareTextTimes(this.businessHoursFields.fridayTo.value, this.businessHoursFields.fridaySecondFrom.value) >= 0) {
        this.businessHoursFields.isBusinessHoursValid = false;
        return;
      }
    }
    if (this.businessHoursFields.saturdaySecondFrom.value) {
      if (!this.businessHoursFields.saturdayTo.value
          || this.compareTextTimes(this.businessHoursFields.saturdayFrom.value, this.businessHoursFields.saturdayTo.value) >= 0
          || this.compareTextTimes(this.businessHoursFields.saturdayTo.value, this.businessHoursFields.saturdaySecondFrom.value) >= 0) {
        this.businessHoursFields.isBusinessHoursValid = false;
        return;
      }
    }

    this.businessHoursFields.isBusinessHoursValid = true;
  }

  private isTimeTextRangeValid(timeTextFrom: string, timeTextTo: string): boolean {
    // An empty range is valid
    if (timeTextFrom && !timeTextTo) {
      return false;
    }
    if (!timeTextFrom && timeTextTo) {
      return false;
    }
    if (timeTextFrom) {
      if (!this.isTextTimeValid(timeTextFrom)) {
        return false;
      }
    }
    if (timeTextTo) {
      if (!this.isTextTimeValid(timeTextTo)) {
        return false;
      }
    }
    return true;
  }

  private isTextTimeValid(timeText: string): boolean {
    if (timeText) {
      const timeComponents: Array<string> = timeText.split(':');
      if (timeComponents.length != 2) {
        return false;
      }
      if (isNaN(Number(timeComponents[0])) || isNaN(Number(timeComponents[1]))) {
        return false;
      }
      const hour = parseInt(timeComponents[0]);
      const minute = parseInt(timeComponents[1]);
      if (hour < 0 || hour > 23) {
        return false;
      }
      if (minute < 0 || minute > 59) {
        return false;
      }
    }
    return true;
  }

  private compareTextTimes(timeTextA: string, timeTextB: string): number {
    if (timeTextA && timeTextB) {
      const timeAAsNumber = parseInt(timeTextA.replace(':', ''));
      const timeBAsNumber = parseInt(timeTextB.replace(':', ''));
      return timeAAsNumber - timeBAsNumber;
    }
    return 0;
  }

  private updateIsSalesTaxValid(): void {
    // 0 is a valid value for sales tax and empty is used to delete the sales tax
    if (this.taxesConfigFields.salesTax.value) {
      const salesTaxNumber = Number(this.taxesConfigFields.salesTax.value);
      if (salesTaxNumber < 0 || salesTaxNumber > 1) {
        this.taxesConfigFields.isSalesTaxValid = false;
        return;
      }
    }

    for (const additionalTax of this.taxesConfigFields.additionalTaxes) {
      const salesTaxNumber = Number(additionalTax?.rate?.value);
      if (salesTaxNumber < 0 || salesTaxNumber > 1) {
        this.taxesConfigFields.isSalesTaxValid = false;
        return;
      }
    }

    this.taxesConfigFields.isSalesTaxValid = true;
  }

  public getText(text: proto.waiternow.common.ITextProto | null | undefined): string {
    return Formatter.getText(
        text,
        this.localizationService.isSpanish() ?
            proto.waiternow.common.Language.SPANISH : proto.waiternow.common.Language.ENGLISH);
  }

  public addAdditionalTax(): void {
    const inputDialogData: AdditionalTaxEditorDialogData = {
    }
    this.additionalTaxEditorDialogService.openEditor(
      inputDialogData,
      /* onUpdate */ outputDialogData => {
      // Updates values in tax. If the rate is removed, delete the tax.
        const additionalTax = new proto.waiternow.common.TaxesProto.TaxProto();
        additionalTax.name = ProtoUtil.createTextProto(outputDialogData.nameEn, outputDialogData.nameEs);
        additionalTax.rate = new proto.waiternow.common.DecimalProto();
        additionalTax.rate.value = outputDialogData.rate;
        this.taxesConfigFields.additionalTaxes.push(additionalTax);
      }
    );
    this.updateIsSalesTaxValid();
  }

  public editAdditionalTax(index: number): void {
    const tax = this.taxesConfigFields.additionalTaxes[index];
    const inputDialogData: AdditionalTaxEditorDialogData = {
      nameEn: Formatter.getNullableText(tax.name, proto.waiternow.common.Language.ENGLISH),
      nameEs: Formatter.getNullableText(tax.name, proto.waiternow.common.Language.SPANISH),
      rate: tax.rate?.value || '0',
    }
    this.additionalTaxEditorDialogService.openEditor(
      inputDialogData,
      /* onUpdate */ outputDialogData => {
      // Updates values in tax. If the rate is removed, delete the tax.
        tax.name = ProtoUtil.createTextProto(outputDialogData.nameEn, outputDialogData.nameEs);
        tax.rate = new proto.waiternow.common.DecimalProto();
        tax.rate.value = outputDialogData.rate;
      }
    );
    this.updateIsSalesTaxValid();
  }

  public deleteAdditionalTax(index: number): void {
    if (index >= 0 && index < this.taxesConfigFields.additionalTaxes.length) {
      this.taxesConfigFields.additionalTaxes.splice(index, 1);
    }
    this.updateIsSalesTaxValid();
  }

  public saveTaxes(actionObserver: ActionObserver): void {
    const locationSettings = this.createLocationSettings();
    const settingsFieldsToRemove: Array<proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField> = [];

    // 0 is a valid value for taxes
    const salesTaxValue = this.taxesConfigFields.salesTax.value;
    const hasSalesTax = salesTaxValue !== null && salesTaxValue !== undefined && salesTaxValue !== '';
    if (!hasSalesTax && this.taxesConfigFields.additionalTaxes.length == 0) {
      settingsFieldsToRemove.push(proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.TAXES);
    } else {
      locationSettings.operationSettings = new proto.waiternow.common.LocationProto.SettingsProto.OperationSettingsProto();
      locationSettings.operationSettings.taxes = new proto.waiternow.common.TaxesProto();
      locationSettings.operationSettings.taxes.salesTax = new proto.waiternow.common.DecimalProto();
      locationSettings.operationSettings.taxes.salesTax.value = hasSalesTax ? salesTaxValue.toString() : '0';
      if (this.taxesConfigFields.salesTaxType.value == this.TAX_TYPE_UNALTERABLE) {
        locationSettings.operationSettings.taxes.salesTaxType = proto.waiternow.common.TaxesProto.Type.UNALTERABLE;
      } else {
        locationSettings.operationSettings.taxes.salesTaxType = proto.waiternow.common.TaxesProto.Type.FALLBACK;
      }
      locationSettings.operationSettings.taxes.additionalTaxes = this.taxesConfigFields.additionalTaxes;
    }

    this.saveSettings(
      locationSettings,
      settingsFieldsToRemove,
      /* onSuccess= */ () => {
        actionObserver.onSuccess(SuccessResult.withMessageTranslateId('saved'));
      },
      /* onError */ error => {
        actionObserver.onError(ErrorResult.withErrorAndMessageTranslateId(error, 'error_updating_taxes'));
      }
    );
  }

  public saveOrdering(actionObserver: ActionObserver): void {
    const locationSettings = this.createLocationSettings();
    const settingsFieldsToRemove: Array<proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField> = [];

    if (!this.orderingFields.orderPreparationDurationMinutes.value) {
      settingsFieldsToRemove.push(
          proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.ORDER_PREPARATION_DURATION);
    } else {
      locationSettings.operationSettings = new proto.waiternow.common.LocationProto.SettingsProto.OperationSettingsProto();
      locationSettings.operationSettings.orderPreparationDuration = ProtoUtil.createDurationProtoFromMillis(this.orderingFields.orderPreparationDurationMinutes.value * 1000 * 60)
    }

    this.saveSettings(
      locationSettings,
      settingsFieldsToRemove,
      /* onSuccess= */ () => {
        actionObserver.onSuccess(SuccessResult.withMessageTranslateId('saved'));
      },
      /* onError */ error => {
        actionObserver.onError(ErrorResult.withErrorAndMessageTranslateId(error, 'error_updating_ordering_configuration'));
      }
    );
  }

  public onBaseFeeChange(money: Money | null | undefined) {
    this.deliveryConfigFields.baseFeeCurrentValue = money;
    this.updateIsSelfConfigurationValid();
  }

  public onBaseDistanceChange(quantity: Quantity | null | undefined) {
    this.deliveryConfigFields.baseDistanceCurrentValue = quantity;
    this.updateIsSelfConfigurationValid();
  }

  public onFeePerDistanceUnitChange(money: Money | null | undefined) {
    this.deliveryConfigFields.feePerDistanceUnitCurrentValue = money;
    this.updateIsSelfConfigurationValid();
  }

  public onMaxDistanceChange(quantity: Quantity | null | undefined) {
    this.deliveryConfigFields.maxDistanceCurrentValue = quantity;
    this.updateIsSelfConfigurationValid();
  }

  public updateIsSelfConfigurationValid(): void {
    if (this.deliveryConfigFields.deliveryType.value != this.SELF_DELIVERY) {
      this.deliveryConfigFields.isSelfConfigurationValid = true;
    } else if (!this.deliveryConfigFields.baseFeeCurrentValue
        || !this.deliveryConfigFields.baseDistanceCurrentValue
        || !this.deliveryConfigFields.feePerDistanceUnitCurrentValue
        || !this.deliveryConfigFields.maxDistanceCurrentValue) {
      this.deliveryConfigFields.isSelfConfigurationValid = false;
    } else {
      this.deliveryConfigFields.isSelfConfigurationValid = true;
    }
  }

  public saveDelivery(actionObserver: ActionObserver): void {
    const locationSettings = this.createLocationSettings();
    const settingsFieldsToRemove: Array<proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField> = [];

    if (this.deliveryConfigFields.deliveryType.value === this.NO_DELIVERY) {
      settingsFieldsToRemove.push(proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.DELIVERY_CONFIG);
    } else {
      locationSettings.operationSettings = new proto.waiternow.common.LocationProto.SettingsProto.OperationSettingsProto();
      locationSettings.operationSettings.deliveryConfig
          = new proto.waiternow.common.LocationProto.SettingsProto.OperationSettingsProto.DeliveryConfigProto();
      if (this.deliveryConfigFields.deliveryType.value === this.DOORDASH_DELIVERY) {
        locationSettings.operationSettings.deliveryConfig.deliveryCarrier = proto.waiternow.common.DeliveryCarrier.DOORDASH;
      } else {
        locationSettings.operationSettings.deliveryConfig.deliveryCarrier = proto.waiternow.common.DeliveryCarrier.SELF;
        locationSettings.operationSettings.deliveryConfig.selfDeliveryConfig
            = new proto.waiternow.common.LocationProto.SettingsProto.OperationSettingsProto.DeliveryConfigProto.SelfDeliveryConfigProto();
        if (this.deliveryConfigFields.baseFeeCurrentValue) {
          locationSettings.operationSettings.deliveryConfig.selfDeliveryConfig.baseFee
              = ProtoUtil.pickerMoneyToMoneyProto(this.deliveryConfigFields.baseFeeCurrentValue);
        }
        if (this.deliveryConfigFields.baseDistanceCurrentValue) {
          locationSettings.operationSettings.deliveryConfig.selfDeliveryConfig.baseDistance
              = ProtoUtil.pickerQuantityToDistanceQuantityProto(this.deliveryConfigFields.baseDistanceCurrentValue);
        }
        if (this.deliveryConfigFields.feePerDistanceUnitCurrentValue) {
          locationSettings.operationSettings.deliveryConfig.selfDeliveryConfig.feePerDistanceUnit
              = ProtoUtil.pickerMoneyToMoneyProto(this.deliveryConfigFields.feePerDistanceUnitCurrentValue);
        }
        if (this.deliveryConfigFields.maxDistanceCurrentValue) {
          locationSettings.operationSettings.deliveryConfig.selfDeliveryConfig.maxDistance
              = ProtoUtil.pickerQuantityToDistanceQuantityProto(this.deliveryConfigFields.maxDistanceCurrentValue);
        }
      }
    }

    this.saveSettings(
      locationSettings,
      settingsFieldsToRemove,
      /* onSuccess= */ () => {
        actionObserver.onSuccess(SuccessResult.withMessageTranslateId('saved'));
      },
      /* onError */ error => {
        actionObserver.onError(ErrorResult.withErrorAndMessageTranslateId(error, 'error_updating_delivery_configuration'));
      }
    );
  }

  public saveExternalLinks(actionObserver: ActionObserver): void {
    const locationSettings = this.createLocationSettings();
    const settingsFieldsToRemove: Array<proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField> = [];

    locationSettings.externalLinks =  new proto.waiternow.common.LocationProto.SettingsProto.ExternalLinksProto();

    if (!this.externalLinksFields.facebook.value && this.externalLinksFields.facebookInitialValue !== '') {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.EXTERNAL_LINKS_FACEBOOK);
    } else {
      if (this.externalLinksFields.facebook.value) {
        locationSettings.externalLinks.facebook = this.externalLinksFields.facebook.value;
      }
    }

    if (!this.externalLinksFields.instagram.value && this.externalLinksFields.instagramInitialValue !== '') {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.EXTERNAL_LINKS_INSTAGRAM);
    } else {
      if (this.externalLinksFields.instagram.value) {
        locationSettings.externalLinks.instagram = this.externalLinksFields.instagram.value;
      }
    }

    if (!this.externalLinksFields.linkedin.value && this.externalLinksFields.linkedinInitialValue !== '') {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.EXTERNAL_LINKS_LINKED_IN);
    } else {
      if (this.externalLinksFields.linkedin.value) {
        locationSettings.externalLinks.linkedIn = this.externalLinksFields.linkedin.value;
      }
    }

    if (!this.externalLinksFields.tiktok.value && this.externalLinksFields.tiktokInitialValue !== '') {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.EXTERNAL_LINKS_TIKTOK);
    } else {
      if (this.externalLinksFields.tiktok.value) {
        locationSettings.externalLinks.tiktok = this.externalLinksFields.tiktok.value;
      }
    }

    if (!this.externalLinksFields.twitter.value && this.externalLinksFields.twitterInitialValue !== '') {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.EXTERNAL_LINKS_TWITTER);
    } else {
      if (this.externalLinksFields.twitter.value) {
        locationSettings.externalLinks.twitter = this.externalLinksFields.twitter.value;
      }
    }

    if (!this.externalLinksFields.youtube.value && this.externalLinksFields.youtubeInitialValue !== '') {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.EXTERNAL_LINKS_YOUTUBE);
    } else {
      if (this.externalLinksFields.youtube.value) {
        locationSettings.externalLinks.youtube = this.externalLinksFields.youtube.value;
      }
    }

    if (!this.externalLinksFields.website.value && this.externalLinksFields.websiteInitialValue !== '') {
      settingsFieldsToRemove.push(
        proto.waiternow.common.UpdateLocationActionProto.Request.SettingsRemovableField.EXTERNAL_LINKS_WEBSITE);
    } else {
      if (this.externalLinksFields.website.value) {
        locationSettings.externalLinks.website = this.externalLinksFields.website.value;
      }
    }

    this.saveSettings(
      locationSettings,
      settingsFieldsToRemove,
      /* onSuccess */ () => {
        actionObserver.onSuccess(SuccessResult.withMessageTranslateId('saved'));
      },
      /* onError */ error => {
        actionObserver.onError(ErrorResult.withErrorAndMessageTranslateId(error, 'error_updating_external_links'));
      }
    );
  }

  public isDefaultAlertsSettings(): boolean {
    return !this.alertConfigFields.email.value && !this.alertConfigFields.phoneNumber.value;
  }

  public isDefaultEnabledFeaturesSettings(): boolean {
    return this.enabledFeaturesFields.pokeWaiter.value
        && this.enabledFeaturesFields.menu.value
        && this.enabledFeaturesFields.imageMenu.value
        && this.enabledFeaturesFields.messageWaiter.value
        && this.enabledFeaturesFields.askForCheck.value
        && this.enabledFeaturesFields.specialInstructionsForMenuItem.value
        && this.enabledFeaturesFields.orderForLater.value
        && this.enabledFeaturesFields.orderForLaterDay.value
        && !this.enabledFeaturesFields.onlinePickupTips.value
        && this.enabledFeaturesFields.onsiteTips.value
        && this.enabledFeaturesFields.delivery.value
        && !this.enabledFeaturesFields.chargeCreditCardFeeToConsumer.value
        && !this.enabledFeaturesFields.alcoholForOnlinePickup.value
        && !this.enabledFeaturesFields.alcoholForDelivery.value
        && !this.enabledFeaturesFields.printOrderAndReceipt.value
        && !this.enabledFeaturesFields.isSmsOnNewOrdersForBusinessWebEnabled.value
        && !this.enabledFeaturesFields.expandQuantityForPrintedOrders.value
        && !this.enabledFeaturesFields.businessAutoStartSession.value;
  }

  public isDefaultViewSettings(): boolean {
    return this.viewFields.theme.value === this.THEME_NONE;
  }

  public isDefaultLocaleSettings(): boolean {
    return this.localeFields.language.value === this.LANGUAGE_NONE;
  }

  public isDefaultBusinessHoursSettings(): boolean {
    return !this.businessHoursFields.sundayFrom.value && !this.businessHoursFields.sundayTo.value
        && !this.businessHoursFields.mondayFrom.value && !this.businessHoursFields.mondayTo.value
        && !this.businessHoursFields.tuesdayFrom.value && !this.businessHoursFields.tuesdayTo.value
        && !this.businessHoursFields.wednesdayFrom.value && !this.businessHoursFields.wednesdayTo.value
        && !this.businessHoursFields.thursdayFrom.value && !this.businessHoursFields.thursdayTo.value
        && !this.businessHoursFields.fridayFrom.value && !this.businessHoursFields.fridayTo.value
        && !this.businessHoursFields.saturdayFrom.value && !this.businessHoursFields.saturdayTo.value
        && !this.businessHoursFields.isSundayClosed.value
        && !this.businessHoursFields.isMondayClosed.value
        && !this.businessHoursFields.isTuesdayClosed.value
        && !this.businessHoursFields.isWednesdayClosed.value
        && !this.businessHoursFields.isThursdayClosed.value
        && !this.businessHoursFields.isFridayClosed.value
        && !this.businessHoursFields.isSaturdayClosed.value;
  }

  public isDefaultTaxesSettings(): boolean {
    const salesTaxValue = this.taxesConfigFields.salesTax.value;
    if (salesTaxValue !== null && salesTaxValue !== undefined && salesTaxValue !== '') {
      return false;
    }
    if (this.taxesConfigFields.salesTaxType.value && this.taxesConfigFields.salesTaxType.value != this.TAX_TYPE_FALLBACK) {
      return false;
    }

    if (this.taxesConfigFields.additionalTaxes.length > 0) {
      return false;
    }

    return true;
  }

  public isDefaultOrderingSettings(): boolean {
    return !this.orderingFields.orderPreparationDurationMinutes.value;
  }

  public isDefaultDeliverySettings(): boolean {
    return (this.deliveryConfigFields.deliveryType.value === this.NO_DELIVERY || this.deliveryConfigFields.deliveryType.value === '')
        && !this.deliveryConfigFields.baseFeeCurrentValue
        && !this.deliveryConfigFields.baseDistanceCurrentValue
        && !this.deliveryConfigFields.feePerDistanceUnitCurrentValue
        && !this.deliveryConfigFields.maxDistanceCurrentValue;
  }

  public isDefaultExternalLinksSettings(): boolean {
    return !this.externalLinksFields.facebook.value
        && !this.externalLinksFields.instagram.value
        && !this.externalLinksFields.linkedin.value
        && !this.externalLinksFields.tiktok.value
        && !this.externalLinksFields.twitter.value
        && !this.externalLinksFields.youtube.value
        && !this.externalLinksFields.website.value
  }

  public calculateTaxes() {
    if (!this.locationId) {
      return;
    }
    const closeDialog = this.dialogService.openProgressDialog();
    this.backendService.getLocationTaxes(
      this.locationId,
      /* onSuccess= */ taxes => {
        closeDialog();
        if (taxes) {
          this.dialogService.openMessageDialog(ProtoUtil.protoToString(taxes));
        }
      },
      /* onError= */ error => {
        closeDialog();
        this.translateService.get('error_internal').subscribe(text => this.dialogService.openMessageDialog(text));
      });
  }
}
